import React, { useEffect, useState } from 'react';
import { useForm, UseFormSetValue } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useParticipantes } from '../../hooks/useParticipantes';
import { enumTipoSeguro, StatusCode, TipoFuncionalidad, TipoSeguro } from '../../../../utils/enums';
import { FechasVigenciaGratuidadSeguro, segurosIncendio } from '../../models/garantias';
import { useGarantias } from '../../hooks/useGarantias';
import { postSeguroIncendio, putSeguroIncendio } from '../../services/servicesGarantias';
import { obtenerFechaGratuidad } from '../../services/servicesParticipantes';
import { useToast } from '../../../../hooks/useToast';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import InputNumber from '../../../../components/inputs/yup/materialui/inputNumber';
import { Box, Grid2, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import ModalPrimaCobertura from '../participantes/modalPrimaCobertura';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';

interface IProps {
    idMutuo: number;
    montoAsegurado: number;
    isComplementario: boolean;
    isDesactivado?: boolean;
    esVigente: boolean;
}

const schema = Yup.object<segurosIncendio>().shape({
    tipoSeguro: Yup.string().required("Campo requerido"),
    codigoPoliza: Yup.string().required("Campo requerido"),
    codigoInterno: Yup.string().required("Campo requerido"),
    tasa: Yup.number().required("Campo requerido"),
    montoPrimaAnual: Yup.number().required("Campo requerido"),
    montoAsegurado: Yup.number().required("Campo requerido"),
    mesesGratuidad: Yup.number().required("Campo requerido"),
    nombreCompania: Yup.string().nullable(),
    montoPrimaDividendo: Yup.number().required("Campo requerido"),
    cantidadCuotasAnuales: Yup.number().required("Campo requerido"),
    tasaSobrePrima: Yup.number().required("Campo requerido"),
});

const SegurosGarantias: React.FC<IProps> = ({ idMutuo, montoAsegurado, isComplementario, isDesactivado, esVigente }) => {

    const { getPolizas, getPolizasInterna, getSeguroParticipante, setPolizasHijo, polizas, polizasHijo } = useParticipantes();
    const { defaultValueSeguro, obtenerSeguroIncendio } = useGarantias();
    const [show, setShow] = useState<boolean>(false);
    const [tienePrima, setTienePrima] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isNew, setIsNew] = useState<boolean>(false);
    const { addToast } = useToast();
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });
    const {
        formState: { errors, isDirty, isValid },
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        trigger
    } = useForm<segurosIncendio>({
        defaultValues: defaultValueSeguro,
        resolver: yupResolver(schema),
        mode: 'onChange',
    })

    const handlePrima = async () => {
        if (data.codigoPoliza && data.codigoInterno) {
            const resp = await getSeguroParticipante(data.codigoPoliza, data.codigoInterno);
            if (resp) {
                setTienePrima(resp.tienePrima!);
            }
        }
    }

    const data = watch();

    useEffect(() => {
        if (!isComplementario) {
            reset(defaultValueSeguro);

            if (defaultValueSeguro?.codigoPoliza)
                getPolizasInterna(defaultValueSeguro?.codigoPoliza, TipoSeguro.INCENDIO.toString());

            setIsNew(defaultValueSeguro.seguroId ? false : true);
            setValue("montoAsegurado", montoAsegurado);

            handlePrima();
        }
        else {
            setDatosSeguroComple(setValue);
        }
    }, [defaultValueSeguro, reset, isComplementario])

    useEffect(() => {
        getPolizas(TipoSeguro.INCENDIO.toString());
        if (!isComplementario) {
            obtenerSeguroIncendio(idMutuo);
        }
        else {
            setDatosSeguroComple(setValue);
        }
    }, [idMutuo, isComplementario])

    const handleCodigoPoliza = (value: string) => {
        if (value)
            getPolizasInterna(value, TipoSeguro.INCENDIO.toString());
        else
            setPolizasHijo([]);

        setValue("codigoInterno", "");
        resetDatosSeguro(setValue);
        setTienePrima(false)
    }

    const handleSeguro = async (codigoInternoPolizaSeleccionado: string) => {

        resetDatosSeguro(setValue);

        const resp = await getSeguroParticipante(data.codigoPoliza, codigoInternoPolizaSeleccionado);

        if (resp) {
            setValue("tasa", resp.tasa!);
            setValue("nombreCompania", resp.nombreCompania);
            setTienePrima(resp.tienePrima!);
        }
    }

    const handleCalcularMonto = async () => {
        if (montoAsegurado && data.tasa) {
            const montoPrima = ((montoAsegurado * data.tasa) / 1000.0);
            const montoPrimaAnual = (montoPrima * 12);

            setValue("montoPrimaDividendo", montoPrima);
            setValue("montoPrimaAnual", montoPrimaAnual);
        } else {
            setValue("montoPrimaDividendo", 0);
            setValue("montoPrimaAnual", 0);
        }
    }

    const handleCancelar = () => {
        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: !isNew ?
                    "¿Desea abandonar la edición?" :
                    '¿Desea abandonar el ingreso?',
                metodo: () => {
                    setIsEditing(false);
                    reset();
                },
            });
        }
        else {
            setIsEditing(false);
            reset();
        }
    }

    useEffect(() => {
        if (!isComplementario) {
            setValue("montoAsegurado", montoAsegurado);

            if (data.tipoSeguro === TipoSeguro.INCENDIO.toString()) {
                handleCalcularMonto();
            }
        }
    }, [data.tasa, data.montoAsegurado, montoAsegurado, isComplementario]);

    const onSubmit = async (data: segurosIncendio) => {
        data.mutuoId = idMutuo;
        data.tipoSeguro = TipoSeguro.INCENDIO.toString();

        // Si no hay meses de gratuidad, vaciar fechas y proceder con la operación
        if (data.mesesGratuidad === 0 || data.mesesGratuidad == null) {
            data.fechaInicio = null;
            data.fechaFin = null;
        } else {
            // Obtener las fechas de gratuidad si existen meses
            const respFecha = await obtenerFechaGratuidad(idMutuo, data.mesesGratuidad);
            if (respFecha.status === StatusCode.Ok) {
                const fechas: FechasVigenciaGratuidadSeguro = respFecha.data;
                data.fechaInicio = fechas.fechaInicio;
                data.fechaFin = fechas.fechaFin;
            } else {
                return;
            }
        }

        const resp = isNew ? await postSeguroIncendio(data) : await putSeguroIncendio(data);
        if (resp.status === StatusCode.Created || resp.status === StatusCode.NoContent || resp.status === StatusCode.Ok) {
            obtenerSeguroIncendio(idMutuo);
            setIsEditing(false);
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: !isNew ?
                    'Datos modificados correctamente' :
                    'Datos guardados correctamente'
            });
        }
    };

    function onHide(): void {
        setShow(false);
    }

    return (
        <Box sx={{ p: 2 }}>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid2 container alignItems="center" spacing={2}>
                    <Grid2 size={2}>
                        <Typography className='card-title' variant="h6">Datos seguro</Typography>
                    </Grid2>

                    {!isComplementario && (
                        !isEditing ? (
                            <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                {
                                    <DetalleMutuoPermissionGuard
                                        esVigente={esVigente}
                                        permissions={
                                            isNew
                                                ? [TipoFuncionalidad.Bandeja_editar]
                                                : [TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info]
                                        }
                                    >
                                        <MButton
                                            variant="contained"
                                            startIcon={!isNew ? <EditIcon /> : undefined}
                                            disabled={isDesactivado}
                                            onClick={() => { setIsEditing(true); trigger(); }}
                                        >
                                            {isNew ? 'Agregar seguro' : 'Editar'}
                                        </MButton>
                                    </DetalleMutuoPermissionGuard>
                                }

                            </Grid2>
                        ) : (
                            <Grid2 container spacing={0} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>
                                <MButton
                                    variant="contained"
                                    startIcon={<SaveAltIcon />}
                                    disabled={!(isDirty && isValid)}
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Guardar
                                </MButton>

                                &nbsp;

                                <MButton
                                    variant='contained'
                                    color='error'
                                    startIcon={<CloseIcon />}
                                    onClick={() => { handleCancelar() }}
                                >
                                    Cancelar
                                </MButton>
                            </Grid2>
                        )
                    )}

                </Grid2>
                <Grid2 container spacing={2} mt={2}>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputText
                            label="Tipo seguro"
                            name="tipoSeguro"
                            control={control}
                            readonly
                            desabilitado={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputSelect
                            label="Código póliza"
                            name="codigoPoliza"
                            control={control}
                            options={polizas}
                            error={errors.codigoPoliza}
                            onChange={(value) => handleCodigoPoliza(value ?? '')}
                            disabled={!isEditing}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputSelect
                            label="Código interno póliza"
                            name="codigoInterno"
                            control={control}
                            options={polizasHijo}
                            error={errors.codigoInterno}
                            onChange={(codigoInternoPolizaSeleccionado) => handleSeguro(codigoInternoPolizaSeleccionado ?? '')}
                            disabled={!isEditing}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Tasa"
                            name="tasa"
                            control={control}
                            error={errors.tasa}
                            decimales={4}
                            readonly={!isEditing}
                            suffix='%'
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Monto prima anual"
                            name="montoPrimaAnual"
                            control={control}
                            error={errors.montoPrimaAnual}
                            decimales={4}
                            readonly
                            esMoneda
                            prefix='UF'
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Monto asegurado"
                            name="montoAsegurado"
                            control={control}
                            error={errors.montoAsegurado}
                            decimales={4}
                            readonly
                            esMoneda
                            prefix='UF'
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Meses gratuidad"
                            name="mesesGratuidad"
                            control={control}
                            error={errors.mesesGratuidad}
                            readonly={!isEditing}
                            maxValue={99}
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputText
                            label="Nombre compañía seguros"
                            name="nombreCompania"
                            control={control}
                            error={errors.nombreCompania}
                            readonly
                            desabilitado={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Monto prima dividendo"
                            name="montoPrimaDividendo"
                            control={control}
                            error={errors.montoPrimaDividendo}
                            decimales={4}
                            readonly
                            esMoneda
                            prefix='UF'
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Cantidad cuotas anuales"
                            name="cantidadCuotasAnuales"
                            control={control}
                            error={errors.cantidadCuotasAnuales}
                            readonly
                            disabled={isComplementario}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, lg: 3 }}>
                        <InputNumber
                            label="Tasa sobreprima"
                            name="tasaSobrePrima"
                            control={control}
                            error={errors.tasaSobrePrima}
                            maxValue={99}
                            decimales={4}
                            readonly={!isEditing}
                            suffix='%'
                            disabled={isComplementario}
                        />
                    </Grid2>

                    {tienePrima && (
                        <Grid2 size={{ xs: 12, lg: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                            <MButton
                                type='button'
                                variant='contained'
                                onClick={() => setShow(true)}
                                disabled={!isEditing}

                            >
                                Prima por cobertura
                            </MButton>
                        </Grid2>
                    )}
                </Grid2>
                <ModalPrimaCobertura
                    show={show}
                    onHide={onHide}
                    codigo={data.codigoPoliza}
                    codigoInterno={data.codigoInterno}
                    tipoSeguro={data.tipoSeguro}
                    tienePrima={tienePrima}
                    montoAsegurado={montoAsegurado}
                />
            </form>
        </Box>
    );
};

export default SegurosGarantias;

function resetDatosSeguro(setValue: UseFormSetValue<segurosIncendio>) {
    setValue("tasa", 0);
    setValue("montoPrimaAnual", 0);
    setValue("mesesGratuidad", 0);
    setValue("nombreCompania", "");
    setValue("montoPrimaDividendo", 0);
}

function setDatosSeguroComple(setValue: UseFormSetValue<segurosIncendio>) {
    setValue("tipoSeguro", TipoSeguro.INCENDIO.toString())
    setValue("nombreCompania", "");
    setValue("montoPrimaDividendo", 0);
    setValue("codigoPoliza", "");
    setValue("codigoInterno", "");
    setValue("tasa", 0);
    setValue("montoPrimaAnual", 0);
    setValue("montoAsegurado", 0);
    setValue("mesesGratuidad", 0);
    setValue("nombreCompania", "");
    setValue("montoPrimaDividendo", 0);
    setValue("cantidadCuotasAnuales", 0);
    setValue("tasaSobrePrima", 0);
}